import { BreakpointConfig, ImageSource } from './responsive-image.types';

/**
 * Generates an array of media query configurations based on the provided breakpoints.
 *
 * Each breakpoint (mobile, tablet, desktop) can define its own media query, sources,
 * and sizes. The function constructs media query objects for each defined breakpoint
 * and returns them as an array.
 *
 * @param breakpoints - An object containing configuration for different breakpoints.
 *   - `mobile` (optional): Configuration for mobile devices.
 *     - `maxWidth` (number): The maximum width for the mobile breakpoint.
 *     - `sources` (string[]): An array of source URLs for the mobile breakpoint.
 *     - `sizes` (string, optional): The sizes attribute for the mobile breakpoint. Defaults to '100vw'.
 *   - `tablet` (optional): Configuration for tablet devices.
 *     - `minWidth` (number): The minimum width for the tablet breakpoint.
 *     - `maxWidth` (number): The maximum width for the tablet breakpoint.
 *     - `sources` (string[]): An array of source URLs for the tablet breakpoint.
 *     - `sizes` (string, optional): The sizes attribute for the tablet breakpoint. Defaults to '100vw'.
 *   - `desktop` (optional): Configuration for desktop devices.
 *     - `minWidth` (number): The minimum width for the desktop breakpoint.
 *     - `sources` (string[]): An array of source URLs for the desktop breakpoint.
 *     - `sizes` (string, optional): The sizes attribute for the desktop breakpoint. Defaults to '100vw'.
 *
 * @returns An array of media query configurations, each containing:
 *   - `media` (string): The media query string.
 *   - `sources` (string[]): An array of source URLs for the respective breakpoint.
 *   - `sizes` (string): The sizes attribute for the respective breakpoint.
 */
export const generateMediaQueries = (breakpoints: BreakpointConfig) => {
  const queries = [];

  if (breakpoints.mobile) {
    queries.push({
      media: `(max-width: ${breakpoints.mobile.maxWidth}px)`,
      sources: breakpoints.mobile.sources,
      sizes: breakpoints.mobile.sizes || '100vw',
    });
  }

  if (breakpoints.tablet) {
    queries.push({
      media: `(min-width: ${breakpoints.tablet.minWidth}px) and (max-width: ${breakpoints.tablet.maxWidth}px)`,
      sources: breakpoints.tablet.sources,
      sizes: breakpoints.tablet.sizes || '100vw',
    });
  }

  if (breakpoints.desktop) {
    queries.push({
      media: `(min-width: ${breakpoints.desktop.minWidth}px)`,
      sources: breakpoints.desktop.sources,
      sizes: breakpoints.desktop.sizes || '100vw',
    });
  }

  return queries;
};

/**
 * Generates a source set string for an array of image sources.
 * The source set string is formatted as a comma-separated list of
 * image source URLs and their corresponding descriptors.
 *
 * @param sources - An array of image sources, where each source
 * contains a `src` (URL of the image) and an optional `descriptor`
 * (e.g., "1x", "2x", or "100w"). If `descriptor` is not provided,
 * it defaults to the width of the image followed by "w".
 *
 * @returns A string representing the source set, suitable for use
 * in the `srcset` attribute of an `<img>` or `<source>` element.
 */
export const generateSourceSet = (sources: ImageSource[]): string => {
  return sources
    .map((source) => {
      const descriptor = source.descriptor || `${source.width}w`;
      return `${source.src} ${descriptor}`;
    })
    .join(', ');
};
